import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, Button, Card, CardContent, Chip, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { ToolCallMessagePartProps } from '@assistant-ui/react';

function parseResult(result: unknown): Record<string, unknown> | null {
  if (result && typeof result === 'object' && !Array.isArray(result)) {
    return result as Record<string, unknown>;
  }
  if (typeof result === 'string') {
    try {
      const parsed = JSON.parse(result);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      // ignore parse error
    }
  }
  return null;
}

function getEntityType(toolName: string): string {
  // Extract entity type from tool name (e.g., createItem -> item, updateBuilding -> building)
  return toolName.replace(/^(create|update)/, '').toLowerCase();
}

function isCreateTool(toolName: string): boolean {
  return toolName.startsWith('create');
}

export const MutationResultTool = ({
  toolName,
  argsText,
  result,
  status,
}: ToolCallMessagePartProps) => {
  const { t } = useTranslation();

  if (status.type === 'running') {
    return (
      <Box display="flex" alignItems="center" gap={1} py={0.5}>
        <CircularProgress size={16} />
        <Typography variant="body2" fontStyle="italic">
          {t('aiChat.tool.running', { action: t(`aiChat.tool.name.${toolName}`, toolName) })}
        </Typography>
      </Box>
    );
  }

  if (status.type === 'requires-action') {
    return (
      <Card elevation={1} sx={{ borderRadius: 2, p: 1 }}>
        <CardContent>
          <Typography variant="body2" mb={1}>
            {t(`aiChat.tool.name.${toolName}`, toolName)}
          </Typography>
          <Box display="flex" gap={1}>
            <Button variant="contained" size="small" autoFocus>
              {t('aiChat.tool.confirm')}
            </Button>
            <Button variant="text" size="small">
              {t('aiChat.tool.cancel')}
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (status.type === 'incomplete') {
    return (
      <Box>
        <Chip
          icon={<ErrorOutlineIcon />}
          label={t('aiChat.tool.failed', { toolName: t(`aiChat.tool.name.${toolName}`, toolName) })}
          color="error"
          size="small"
          variant="outlined"
        />
        {argsText && (
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            {argsText}
          </Typography>
        )}
      </Box>
    );
  }

  // Complete state: render success/error chip
  const entity = parseResult(result);
  // eslint-disable-next-line @typescript-eslint/no-base-to-string
  const entityName = entity ? String(entity.name ?? entity.id ?? '') : '';
  const entityType = getEntityType(toolName);
  const isCreate = isCreateTool(toolName);

  const isError = entity && ('error' in entity || ('message' in entity && !('name' in entity)));

  if (isError) {
    // eslint-disable-next-line @typescript-eslint/no-base-to-string
    const errorMsg = String(entity.error ?? entity.message ?? '');
    return (
      <Chip
        icon={<ErrorOutlineIcon />}
        label={
          errorMsg ||
          t('aiChat.tool.failed', { toolName: t(`aiChat.tool.name.${toolName}`, toolName) })
        }
        color="error"
        size="small"
        variant="outlined"
      />
    );
  }

  const label = isCreate
    ? t('aiChat.tool.mutation.created', { entityType, name: entityName })
    : t('aiChat.tool.mutation.updated', { entityType, name: entityName });

  return (
    <Box>
      <Chip label={label} color="success" size="small" variant="outlined" />
    </Box>
  );
};
